/**
 * Default salary currency for a new offer (CORE-4740).
 *
 * Resolved client-side from the job payload already in Redux
 * (`currentJob.data`), mirroring how `ats_office_id` is prefilled in
 * `offerFormHelpers`. There is no backend default for the web path: the form
 * always sends `salary_currency_id`, so a server-side default would never fire.
 *
 * The office the offer is for decides the currency, then the org-wide default
 * from Settings -> Organization -> General, then USD.
 */

export interface JobOffice {
  id?: number;
  /** The office's country. NOT guaranteed to be a selectable currency id. */
  places_country_id?: number | null;
  compensation?: {
    enabled?: boolean;
    /**
     * Per-office account default. Unlike the top-level `compensation` block
     * this is not gated on `compensation.enabled`, so it survives for accounts
     * with compensation disabled — prefer it over the deprecated top-level one.
     */
    default_currency_country_id?: number | null;
  } | null;
}

export interface JobForCurrency {
  id?: number | string | null;
  basic_info?: {
    office?: JobOffice[] | null;
  } | null;
}

const isUsable = (id: unknown): id is number =>
  typeof id === 'number' && Number.isFinite(id);

/**
 * The Redux job, but only when it is actually THIS offer's job.
 *
 * `currentJob` is a single shared slot written by the job-details and
 * job-creation screens (`getCurrentJob`); nothing in the candidate drawer
 * refreshes it. Open an offer from a candidate profile, global search or a
 * dashboard task and that slot still holds whichever job was last visited — so
 * reading a currency off it can prefill a DIFFERENT job's currency, which is
 * worse than not prefilling at all.
 *
 * The application payload carries only `ats_job: { id, name }`, which is enough
 * to tell the two apart. A missing or mismatched id yields `undefined`, and the
 * caller falls through to the USD floor — the behaviour before CORE-4740.
 */
export const jobForApplication = (
  currentJob?: JobForCurrency | null,
  applicationJobId?: number | string | null
): JobForCurrency | undefined => {
  if (!currentJob || !applicationJobId || !currentJob.id) return undefined;
  return String(currentJob.id) === String(applicationJobId)
    ? currentJob
    : undefined;
};

/**
 * First candidate that is both set and offered by the currency list.
 *
 * `allowedCurrencyIds` is the id set from `GET /compensations/currencies`,
 * which is deduplicated to one row per ISO code — several countries share a
 * currency but only one id per currency is selectable, and the backend rejects
 * any other country id. An id outside that set would render the select blank
 * and 404 on save, so such a candidate is skipped rather than used.
 *
 * When the list hasn't loaded yet the guard is skipped entirely: an empty set
 * means "unknown", not "nothing is allowed".
 */
export const firstAllowedCurrencyId = (
  candidates: Array<number | null | undefined>,
  allowedCurrencyIds?: Set<number>
): number | undefined => {
  const hasAllowList = !!allowedCurrencyIds && allowedCurrencyIds.size > 0;
  return candidates.find(
    (id): id is number =>
      isUsable(id) && (!hasAllowList || allowedCurrencyIds.has(id))
  );
};

/**
 * Resolution order, first non-null wins:
 *
 *   1. `basic_info.office[selected].places_country_id` — the office's country
 *   2. `basic_info.office[0].compensation.default_currency_country_id`
 *   3. `accountDefaultCurrencyId`                      — Settings -> General
 *   4. `undefined`                                     — caller applies USD
 *
 * Office-led, matching the ticket: where the role is based decides the payroll
 * currency. The job's own salary-band currency
 * (`compensation.base_salary.currency_country_id`) is deliberately NOT read —
 * a job posted in one currency does not imply the offer is paid in it.
 *
 * Rungs 1-2 read the per-office `compensation` block, which — unlike the
 * top-level one — is not gated on `compensation.enabled`, so they still
 * resolve for accounts with compensation switched off.
 *
 * Rung 3 is the org-wide default from `GET /account`, the same value the
 * requisition form uses. It answers the two cases the office rungs can't: a job
 * whose offices configure nothing, and a drawer opened away from the job screens
 * where the Redux job has been dropped as not-this-job.
 *
 * `undefined` means "nothing configured anywhere". The USD floor is applied by
 * `getInitializeOfferValues` (`?? USD_CURRENCY_COUNTRY_ID`), which keeps the
 * fallback in one place and keeps this resolver testable on its own.
 */
export const resolveOfferDefaultCurrencyId = ({
  currentJob,
  selectedOfficeId,
  accountDefaultCurrencyId,
  allowedCurrencyIds,
}: {
  currentJob?: JobForCurrency | null;
  /** The office the offer form prefilled; falls back to the job's first. */
  selectedOfficeId?: number | null;
  /** `account.default_currency_country_id` from `GET /account`. */
  accountDefaultCurrencyId?: number | null;
  allowedCurrencyIds?: Set<number>;
}): number | undefined => {
  const offices = currentJob?.basic_info?.office || [];

  const selectedOffice =
    (isUsable(selectedOfficeId)
      ? offices.find((office) => office?.id === selectedOfficeId)
      : undefined) || offices[0];

  return firstAllowedCurrencyId(
    [
      selectedOffice?.places_country_id,
      offices[0]?.compensation?.default_currency_country_id,
      accountDefaultCurrencyId,
    ],
    allowedCurrencyIds
  );
};

/** Currency-list rows → the id set the guard above checks against. */
export const toAllowedCurrencyIds = (
  currencies?: Array<{ id?: number | null }> | null
): Set<number> =>
  new Set((currencies || []).map((c) => c?.id).filter(isUsable));
